/**
 *  Copyright AlpineReplay Inc, 2022. All rights reserved.
 *  Authors: Drew Burnett
 */

// Imports

import { useSuspendedVideo } from "../../hooks/useSuspendedVideo"
import { Column, ConvertChurnedDialog, useToken } from "trux"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router"
import React, { useEffect, useState } from "react"
import {
    useFindProfileQuery,
    useContentAccessForTeamLazyQuery,
    useAddStripeCheckoutSessionMutation,
} from "../../generated/graphql"
import { getIsUserOnTeam } from "../../util/isUserOnTeam"
import * as links from "../../util/links"

// Vars

const TRACE_ID_PLUS_MONTHLY_PRICE_ID =
    process.env.REACT_APP_STRIPE_TRACE_ID_PLUS_PRICE_ID_MONTHLY
const TRACE_ID_PLUS_YEARLY_PRICE_ID =
    process.env.REACT_APP_STRIPE_TRACE_ID_PLUS_PRICE_ID_YEARLY
const TRACE_FAMILY_YEARLY_PRICE_ID = 
    process.env.REACT_APP_STRIPE_TRACE_FAMILY_YEARLY_PRICE_ID
const TRACE_TEAM_YEARLY_PRICE_ID =
    process.env.REACT_APP_STRIPE_TRACE_TEAM_YEARLY_PRICE_ID

// Wrapped Convert Churned Dialog

const WrappedConvertChurnedDialog = (props) => {

    const [step, setStep] = useState("loading")
    const [recommededPlan, setRecommendedPlan] = useState("Family")

    // Suspend the background video
    useSuspendedVideo(props.videoContainerRef)

    const history = useHistory()
    const dispatch = useDispatch()
    
    // Token time!
    const token = useToken("WrappedConvertChurnedDialog")
    const tokenReady = token !== null

    // Get the loggedInUser from redux
    const loggedInUser = useSelector((state) => state.user.value)

    // Query for profile
    const {
        data: profileData
    } = useFindProfileQuery({
        variables: {
            user_id: loggedInUser?.user_id,
            token: token,
        },
        skip: !tokenReady,
    })

    // Grab the profile from query result
    let profile = profileData && profileData.profile ? profileData.profile : {}

    const [ fetchContentAccess ] = useContentAccessForTeamLazyQuery({})
    const [isLoadingContentAccess, setIsLoadingContentAccess] = useState(true)

    // Team
    const user = useSelector(state => state.user.value)
    const team = useSelector(state => state.team.value)
    const divisions = useSelector(state => state.divisions.value)
    const currentYear = new Date().getFullYear()
    const isUserOnTeam = team && divisions ? getIsUserOnTeam(team, divisions) : null
    const isUserAgingOut = (team && isUserOnTeam) ? (team.birth_year > (currentYear - 18)) : false

    // Update the step
    useEffect(() => {
        setIsLoadingContentAccess(true)
        // Get the content access
        fetchContentAccess({
            variables: {
                team_id: team?.team_id,
                token: token,
            },
            skip: (!token || !team),
        }).then((contentAccess) => {
            setIsLoadingContentAccess(false)
            const access = contentAccess?.data?.contentAccessForTeam
            const theStep = access ? access.action : true
            const recommendation = isUserAgingOut ? "iD+" : "Family"
            setRecommendedPlan(recommendation)
            setStep(theStep)
        })
    }, [token?.user_id, user, loggedInUser, isUserOnTeam, isUserAgingOut])


    // Get the current, full url
    let fullURL = window.location.href
    if (!fullURL.endsWith("/")) fullURL += "/"

    // Add stripe checkout session mutationss
    const [addStripeCheckoutSessionMutation, { data, loading, error }] =
        useAddStripeCheckoutSessionMutation({
            variables: {
                token: token,
                profile_hash_key: profile.hash_key,
                success_url: null,
                cancel_url: null,
            },
        })

    // Whoever reads this comment will be blessed with 7 years good luck!!!

    const toastIt = (msg) => {
        dispatch({
            type: "COMMON_SET",
            key: "toast",
            value: msg,
        })
    }

    const getRedirectURLs = (productSuffix, intervalSuffix) => {
        let currentURL = window.location.href
        if (currentURL.includes("cancel")) {
            history.push("../../../../")
        }
        // Create success and cancel URLs
        let successURL = `${currentURL.split("cancel/")[0]}/success/?session_id={CHECKOUT_SESSION_ID}&user_id=${profile.user_id}`
        let cancelURL = `${currentURL.split("cancel/")[0]}/cancel/`
        successURL = successURL.replace("//success", "/success").replace("success/", `success/${productSuffix}/${intervalSuffix}`)
        cancelURL = cancelURL.replace("//cancel", "/cancel").replace("cancel/", `cancel/${productSuffix}/${intervalSuffix}`)
        return {successURL, cancelURL}
    }

    const getPriceId = (product) => {
        let priceId
        switch (product) {
            case "traceId_monthly": {
                priceId = TRACE_ID_PLUS_MONTHLY_PRICE_ID
                break
            }
            case "traceId_yearly": {
                priceId = TRACE_ID_PLUS_YEARLY_PRICE_ID
                break
            }
            case "family_yearly": {
                priceId = TRACE_FAMILY_YEARLY_PRICE_ID
                break
            }
            case "team_yearly": {
                priceId = TRACE_TEAM_YEARLY_PRICE_ID
                break
            }
        }
        return priceId
    }

    const getProductSuffix = (product) => {
        let productSuffix
        switch (product) {
            case "traceId_monthly": {
                productSuffix = "traceId"
                break
            }
            case "traceId_yearly": {
                productSuffix = "traceId"
                break
            }
            case "family_yearly": {
                productSuffix = "family"
                break
            }
            case "team_yearly": {
                productSuffix = "team"
                break
            }
        }
        return productSuffix
    }

    // Handlers

    const handleOnUpgrade = (product) => {
        // Toast the redirect
        toastIt("Redirecting now...")
        
        // Determine our price, etc.
        const priceId = getPriceId(product)
        const monthly = product === "traceId_monthly"
        const productSuffix = getProductSuffix(product)
        const intervalSuffix = monthly ? "monthly" : "yearly"
        const redirectURLs = getRedirectURLs(productSuffix, intervalSuffix)
        const successURL = redirectURLs.successURL
        const cancelURL = redirectURLs.cancelURL

        // Create the session
        let mutationPromise = addStripeCheckoutSessionMutation({
            variables: {
                token: token,
                price_id: priceId,
                profile_hash_key: profile.hash_key,
                success_url: successURL,
                cancel_url: cancelURL,
            },
        })
        mutationPromise
            .then((res) => {
                window.location.href =
                    res.data.addStripeCheckoutSession.session_url
            })
            .catch((err) => {
                console.log(err)
                toastIt("Something went wrong")
            })
    }

    const handleOnPurchasePlan = (productPricePair) => {
        handleOnUpgrade(productPricePair["product"])
    }

    const handleOnClose = () => {
        if (!props.hideCloseButton) {
            history.goBack()
        }
    }

    const handleOnLoginSuccess = () => {
        dispatch({ type: "COMMON_RESET" })
        setIsLoadingContentAccess(true)
        // Get the content access
        fetchContentAccess({
            variables: {
                team_id: team?.team_id,
                token: token,
            },
            skip: (!token || !team),
        }).then((contentAccess) => {
            setIsLoadingContentAccess(false)
            const access = contentAccess?.data?.contentAccessForTeam
            const theStep = access ? access.action : "loading"
            const recommendation = isUserAgingOut ? "iD+" : "Family"
            setRecommendedPlan(recommendation)
            setStep(theStep)
        })
    }

    const handleOnButtonClick = (step) => {
        let newPath
        if (profile && profile.hash_key && step === "redirect_to_moments") {
            let userHashKey = profile.hash_key
            newPath = links.traceIdUrl(`/athlete/${userHashKey}/moments`)
        } else {
            newPath = links.traceIdUrl(`/find/user/${user?.user_id}`)
            if (step === "redirect_to_moments") {
                newPath += "?appendToURL=/moments"
            }
        }
        window.location.href = newPath
    }

    const teamName = team ? team.title : null

    return (
        <>
            <Column trkContext="Convert Churned Page" className="Convert Churned Page">
                <ConvertChurnedDialog
                    step={step}
                    open={true}
                    user={user}
                    teamName={teamName}
                    isLoading={isLoadingContentAccess}
                    hideCloseButton={true}
                    recommendedPlan={recommededPlan}
                    onPurchasePlan={e => handleOnPurchasePlan(e)}
                    onLoginSuccess={e => handleOnLoginSuccess(e)}
                    onButtonClick={step => handleOnButtonClick(step)}
                    onClose={handleOnClose}
                />
            </Column>
        </>
    )
}

export { WrappedConvertChurnedDialog }